<svelte:options immutable={true} />

<script>
  import { createEventDispatcher, onMount } from "svelte";
  import { pannable } from "../../actions/pannable.js";
  import { tapout } from "../../actions/tapout.js";
  import Toolbar from "../Toolbar.svelte";
  import { editor } from "../../stores/editor.js";

  export let shapeType = "rect";
  export let width;
  export let height;
  export let x;
  export let y;
  export let fillColor = "transparent";
  export let strokeColor = "#000000";
  export let strokeWidth = 5;
  export let borderRadius = 0;
  export let opacity = 1;
  export let keepRatio = false;
  export let pageScale = 1;
  export let selected = false;
  export let showToolbar = true;
  export let hideBorders = false;

  const dispatch = createEventDispatcher();

  let wasEditing = false;
  $: {
    if (isEditing !== wasEditing) {
      if (isEditing) dispatch("select");
      else dispatch("blur");
      wasEditing = isEditing;
    }
  }

  let startPointerX = 0;
  let startPointerY = 0;
  let isEditing = false;
  let isHovered = false;

  let operation = "";
  let resizeDirection = "";

  let translateX = 0;
  let translateY = 0;
  let draftOpacity = opacity;
  let deltaWidth = 0;
  let deltaHeight = 0;

  let aspectRatio = null;

  function lockAspectRatio(deltaWidth, deltaHeight, aspectRatio) {
    const deltaHeightFromWidth = (width + deltaWidth) / aspectRatio - height;
    if (deltaHeight > deltaHeightFromWidth) {
      const deltaWidthFromHeight = (height + deltaHeight) * aspectRatio - width;
      return [deltaWidthFromHeight, deltaHeight];
    }
    return [deltaWidth, deltaHeightFromWidth];
  }

  function onPanStart(event) {
    isEditing = true;
    startPointerX = event.detail.x;
    startPointerY = event.detail.y;

    if (event.detail.target === event.currentTarget) {
      operation = "move";
      return;
    }

    operation = "scale";
    resizeDirection = event.detail.target.dataset.direction || "";
  }

  function onPanMove(event) {
    const pointerDeltaX = (event.detail.x - startPointerX) / pageScale;
    const pointerDeltaY = (event.detail.y - startPointerY) / pageScale;

    if (operation === "move") {
      translateX = pointerDeltaX;
      translateY = pointerDeltaY;
      return;
    }

    if (operation !== "scale") return;

    const fromLeft = resizeDirection.includes("left");
    const fromRight = resizeDirection.includes("right");
    const fromTop = resizeDirection.includes("top");
    const fromBottom = resizeDirection.includes("bottom");
    const hasHorizontal = fromLeft || fromRight;
    const hasVertical = fromTop || fromBottom;

    deltaWidth = fromLeft ? -pointerDeltaX : fromRight ? pointerDeltaX : 0;
    deltaHeight = fromTop ? -pointerDeltaY : fromBottom ? pointerDeltaY : 0;

    let currentAspectRatio = aspectRatio;
    if (keepRatio && currentAspectRatio === null) {
      currentAspectRatio = width / height;
    }

    if (currentAspectRatio !== null) {
      if (hasHorizontal && !hasVertical) {
        deltaHeight = (width + deltaWidth) / currentAspectRatio - height;
      } else if (hasVertical && !hasHorizontal) {
        deltaWidth = (height + deltaHeight) * currentAspectRatio - width;
      } else if (hasHorizontal && hasVertical) {
        [deltaWidth, deltaHeight] = lockAspectRatio(
          deltaWidth,
          deltaHeight,
          currentAspectRatio,
        );
      }
    }

    if (width + deltaWidth < 10) {
      deltaWidth = 10 - width;
      if (currentAspectRatio !== null)
        deltaHeight = (width + deltaWidth) / currentAspectRatio - height;
    }
    if (height + deltaHeight < 10) {
      deltaHeight = 10 - height;
      if (currentAspectRatio !== null)
        deltaWidth = (height + deltaHeight) * currentAspectRatio - width;
    }

    translateX = fromLeft ? -deltaWidth : 0;
    translateY = fromTop ? -deltaHeight : 0;
  }

  function onPanEnd() {
    if (operation === "move") {
      dispatch("update", {
        x: x + translateX,
        y: y + translateY,
      });
    } else if (operation === "scale") {
      dispatch("update", {
        x: x + translateX,
        y: y + translateY,
        width: width + deltaWidth,
        height: height + deltaHeight,
      });
    }

    operation = "";
    resizeDirection = "";
    translateX = 0;
    translateY = 0;
    deltaWidth = 0;
    deltaHeight = 0;
  }

  function onBlur() {
    isEditing = false;
  }

  function changeType(e) {
    dispatch("update", { shapeType: e.target.value });
  }

  function changeStrokeColor(e) {
    dispatch("update", { strokeColor: e.target.value });
  }

  function changeFillColor(e) {
    dispatch("update", { fillColor: e.target.value });
  }

  function setFillTransparent() {
    dispatch("update", { fillColor: "transparent" });
  }

  function setStrokeTransparent() {
    dispatch("update", { strokeColor: "transparent" });
  }

  function toggleKeepRatio() {
    dispatch("update", { keepRatio: !keepRatio });
  }

  function changeThickness(e) {
    dispatch("update", { strokeWidth: parseInt(e.target.value) || 0 });
  }

  function changeRadius(e) {
    dispatch("update", { borderRadius: parseInt(e.target.value) || 0 });
  }

  onMount(() => {
    function onKeyDown(event) {
      if (event.key === "Shift") {
        aspectRatio = (width + deltaWidth) / (height + deltaHeight);
      }
    }
    function onKeyUp(event) {
      if (event.key === "Shift") {
        aspectRatio = null;
      }
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  });

  $: currentWidth = width + deltaWidth;
  $: currentHeight = height + deltaHeight;
  $: sw = strokeWidth;

  function getStarPoints(w, h, sw) {
    const cx = w / 2;
    const cy = h / 2;
    const outerRadius = Math.min(w, h) / 2 - sw;
    const innerRadius = outerRadius / 2;
    let points = [];
    for (let i = 0; i < 10; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = (i * Math.PI) / 5 - Math.PI / 2;
      points.push(
        `${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`,
      );
    }
    return points.join(" ");
  }

  function getXShapePoints(w, h, sw) {
    const t = Math.min(w, h) * 0.15;
    const tx = t;
    const ty = t * (h / w);
    const left = sw,
      right = w - sw,
      top = sw,
      bottom = h - sw;
    const cx = w / 2,
      cy = h / 2;
    return [
      `${left},${top + ty}`,
      `${cx - tx},${cy}`,
      `${left},${bottom - ty}`,
      `${left + tx},${bottom}`,
      `${cx},${cy + ty}`,
      `${right - tx},${bottom}`,
      `${right},${bottom - ty}`,
      `${cx + tx},${cy}`,
      `${right},${top + ty}`,
      `${right - tx},${top}`,
      `${cx},${cy - ty}`,
      `${left + tx},${top}`,
    ].join(" ");
  }
</script>

{#if isEditing && showToolbar}
  <Toolbar>
    <div class="flex items-center gap-3 px-1">
      <div class="flex items-center gap-2 pr-2">
        <select
          value={shapeType}
          on:change={changeType}
          class="text-xs font-semibold px-2 py-1 bg-gray-100 rounded border-none outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="rect">方形</option>
          <option value="circle">圓形</option>
          <option value="star">星型</option>
          <option value="x">X</option>
          <option value="line">直線</option>
        </select>
      </div>

      <div
        class="flex items-center gap-1.5 border-l border-gray-200 pl-3 pr-2"
        title="Stroke Color"
      >
        <span
          class="text-[10px] text-gray-500 font-bold uppercase tracking-wider"
          >外框</span
        >
        <input
          type="color"
          value={strokeColor === "transparent" ? "#000000" : strokeColor}
          on:input={changeStrokeColor}
          class="w-6 h-6 rounded border-none cursor-pointer p-0 shrink-0"
        />
        <div class="flex items-center gap-1 ml-1 hide-on-small">
          <button
            title="透明"
            on:click={setStrokeTransparent}
            class="w-4 h-4 rounded-full border border-gray-300 shadow-sm relative overflow-hidden bg-white"
            ><div
              class="absolute inset-0 bg-red-500 w-0.5 h-full transform rotate-45 mx-auto"
            ></div></button
          >
          <button
            type="button"
            class="w-4 h-4 rounded-full border border-gray-200 bg-black shadow-sm"
            on:click={() => dispatch("update", { strokeColor: "#000000" })}
          ></button>
          <button
            type="button"
            class="w-4 h-4 rounded-full border border-gray-300 bg-white shadow-sm"
            on:click={() => dispatch("update", { strokeColor: "#FFFFFF" })}
          ></button>
          <button
            type="button"
            class="w-4 h-4 rounded-full border border-gray-200 shadow-sm bg-red-500"
            on:click={() => dispatch("update", { strokeColor: "#EF4444" })}
          ></button>
          <button
            type="button"
            class="w-4 h-4 rounded-full border border-gray-200 shadow-sm bg-blue-500"
            on:click={() => dispatch("update", { strokeColor: "#3B82F6" })}
          ></button>
          <button
            type="button"
            class="w-4 h-4 rounded-full border border-gray-200 shadow-sm bg-green-500"
            on:click={() => dispatch("update", { strokeColor: "#22C55E" })}
          ></button>
        </div>
      </div>

      <div
        class="flex items-center gap-1.5 border-l border-gray-200 pl-3 pr-2"
        title="Fill Color"
      >
        <span
          class="text-[10px] text-gray-500 font-bold uppercase tracking-wider"
          >填色</span
        >
        <input
          type="color"
          value={fillColor === "transparent" ? "#ffffff" : fillColor}
          on:input={changeFillColor}
          class="w-6 h-6 rounded border-none cursor-pointer p-0 shrink-0"
        />
        <div class="flex items-center gap-1 ml-1 hide-on-small">
          <button
            title="透明"
            on:click={setFillTransparent}
            class="w-4 h-4 rounded-full border border-gray-300 shadow-sm relative overflow-hidden bg-white"
            ><div
              class="absolute inset-0 bg-red-500 w-0.5 h-full transform rotate-45 mx-auto"
            ></div></button
          >
          <button
            type="button"
            class="w-4 h-4 rounded-full border border-gray-200 bg-black shadow-sm"
            on:click={() => dispatch("update", { fillColor: "#000000" })}
          ></button>
          <button
            type="button"
            class="w-4 h-4 rounded-full border border-gray-300 bg-white shadow-sm"
            on:click={() => dispatch("update", { fillColor: "#FFFFFF" })}
          ></button>
          <button
            type="button"
            class="w-4 h-4 rounded-full border border-gray-200 shadow-sm bg-red-500"
            on:click={() => dispatch("update", { fillColor: "#EF4444" })}
          ></button>
          <button
            type="button"
            class="w-4 h-4 rounded-full border border-gray-200 shadow-sm bg-blue-500"
            on:click={() => dispatch("update", { fillColor: "#3B82F6" })}
          ></button>
          <button
            type="button"
            class="w-4 h-4 rounded-full border border-gray-200 shadow-sm bg-green-500"
            on:click={() => dispatch("update", { fillColor: "#22C55E" })}
          ></button>
        </div>
      </div>

      <div class="flex items-center gap-2 border-l border-gray-200 pl-3">
        <span
          class="text-[10px] text-gray-500 font-bold uppercase tracking-wider"
          >粗細</span
        >
        <input
          type="number"
          min="0"
          max="50"
          value={strokeWidth}
          on:input={changeThickness}
          class="w-10 h-6 text-xs text-center border-gray-300 rounded"
        />
      </div>

      {#if shapeType === "rect"}
        <div class="flex items-center gap-2 border-l border-gray-200 pl-3">
          <span
            class="text-[10px] text-gray-500 font-bold uppercase tracking-wider"
            >圓角</span
          >
          <input
            type="number"
            min="0"
            max="100"
            value={borderRadius}
            on:input={changeRadius}
            class="w-10 h-6 text-xs text-center border-gray-300 rounded"
          />
        </div>
      {/if}

      <div class="flex items-center border-l border-gray-200 pl-3 ml-1">
        <button
          on:click={toggleKeepRatio}
          class="w-6 h-6 flex items-center justify-center rounded transition-colors hover:bg-gray-100 {keepRatio
            ? 'text-indigo-600'
            : 'text-gray-400'}"
          title="維持圖形比例"
        >
          {#if keepRatio}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              ><rect x="3" y="11" width="18" height="11" rx="2" ry="2"
              ></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg
            >
          {:else}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              ><rect x="3" y="11" width="18" height="11" rx="2" ry="2"
              ></rect><path d="M7 11V7a5 5 0 0 1 9.9-1"></path></svg
            >
          {/if}
        </button>
      </div>
      <div class="flex items-center gap-2 border-l border-gray-200 px-3">
        <span class="text-xs text-gray-500">透明度:</span>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          bind:value={draftOpacity}
          on:input={() => dispatch("update", { opacity: Number(draftOpacity) })}
          class="w-16 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          title="調整透明度"
        />
      </div>
    </div>
  </Toolbar>
{/if}

<div
  use:tapout
  on:tapout={onBlur}
  on:mouseenter={() => (isHovered = true)}
  on:mouseleave={() => (isHovered = false)}
  on:mousedown={() => (isEditing = true)}
  on:touchstart={() => (isEditing = true)}
  class="absolute left-0 top-0 select-none pointer-events-auto"
  class:active-element={isEditing || isHovered}
  style="width: {currentWidth}px; height: {currentHeight}px; transform: translate({x +
    translateX}px, {y + translateY}px); opacity: {draftOpacity};"
>
  <svg
    viewBox="0 0 {currentWidth} {currentHeight}"
    width="100%"
    height="100%"
    preserveAspectRatio="none"
  >
    {#if shapeType === "rect"}
      <rect
        x={sw / 2}
        y={sw / 2}
        width={Math.max(0, currentWidth - sw)}
        height={Math.max(0, currentHeight - sw)}
        fill={fillColor}
        stroke={strokeColor}
        stroke-width={sw}
        rx={borderRadius}
      />
    {:else if shapeType === "circle"}
      <ellipse
        cx={currentWidth / 2}
        cy={currentHeight / 2}
        rx={Math.max(0, currentWidth / 2 - sw / 2)}
        ry={Math.max(0, currentHeight / 2 - sw / 2)}
        fill={fillColor}
        stroke={strokeColor}
        stroke-width={sw}
      />
    {:else if shapeType === "star"}
      <polygon
        points={getStarPoints(currentWidth, currentHeight, sw)}
        fill={fillColor}
        stroke={strokeColor}
        stroke-width={sw}
        stroke-linejoin="round"
      />
    {:else if shapeType === "x"}
      <polygon
        points={getXShapePoints(currentWidth, currentHeight, sw)}
        fill={fillColor}
        stroke={strokeColor}
        stroke-width={sw}
        stroke-linejoin="round"
      />
    {:else if shapeType === "line"}
      <rect
        x={sw / 2}
        y={currentHeight / 2 - Math.min(currentWidth, currentHeight) * 0.1}
        width={Math.max(0, currentWidth - sw)}
        height={Math.max(0, Math.min(currentWidth, currentHeight) * 0.2)}
        fill={fillColor}
        stroke={strokeColor}
        stroke-width={sw}
        rx={Math.min(currentWidth, currentHeight) * 0.1}
      />
    {/if}
  </svg>

  <div
    use:pannable
    on:panstart={onPanStart}
    on:panmove={onPanMove}
    on:panend={onPanEnd}
    class="absolute w-full h-full left-0 top-0 cursor-grab z-10"
    class:cursor-grabbing={operation === "move"}
    class:operation={isEditing && !operation}
  >
    {#if !hideBorders || selected || isEditing}
      <div
        data-direction="left"
        class="resize-border h-full w-1 left-0 top-0 border-l cursor-ew-resize"
      />
      <div
        data-direction="top"
        class="resize-border w-full h-1 left-0 top-0 border-t cursor-ns-resize"
      />
      <div
        data-direction="bottom"
        class="resize-border w-full h-1 left-0 bottom-0 border-b cursor-ns-resize"
      />
      <div
        data-direction="right"
        class="resize-border h-full w-1 right-0 top-0 border-r cursor-ew-resize"
      />
      <div
        data-direction="left-top"
        class="resize-corner left-0 top-0 cursor-nwse-resize transform -translate-x-1/2 -translate-y-1/2 md:scale-25"
      />
      <div
        data-direction="right-top"
        class="resize-corner right-0 top-0 cursor-nesw-resize transform translate-x-1/2 -translate-y-1/2 md:scale-25"
      />
      <div
        data-direction="left-bottom"
        class="resize-corner left-0 bottom-0 cursor-nesw-resize transform -translate-x-1/2 translate-y-1/2 md:scale-25"
      />
      <div
        data-direction="right-bottom"
        class="resize-corner right-0 bottom-0 cursor-nwse-resize transform translate-x-1/2 translate-y-1/2 md:scale-25"
      />
    {/if}
  </div>
</div>

<style>
  .operation {
    background-color: rgba(0, 0, 0, 0.05);
  }
  .resize-border {
    @apply absolute border-dashed border-gray-600;
  }
  .resize-corner {
    @apply absolute w-10 h-10 bg-blue-300 rounded-full bg-opacity-50;
  }
  .active-element {
    outline: 2px dashed #3b82f6;
    outline-offset: 4px;
  }
</style>
